/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import * as yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormProvider, useForm } from 'react-hook-form';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';

import DNStep from '../deposit-now-step/dn-step';
import cn from 'core/utils/cn';
import toastFunctions from 'lib-ui/toast/Toast';
import useAgreementsList from 'core/api/client-portal/hooks/agreements/use-agreements-list';
import useCreateDeposit from 'core/api/client-portal-v2/hooks/deposits/use-create-deposit';

type FormData = {
    relatedAgreements: string[];
};

const AgreementStep = () => {
    const { t } = useTranslation();
    const goTo = useNavigate();
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCountry, setSelectedCountry] = useState('All');
    const [selectedType, setSelectedType] = useState('All');
    const [selectedAll, setSelectedAll] = useState(false);
    const [filter, setFilter] = useState('none');

    const { data, isLoading } = useAgreementsList({
        DepositNow: true
    });

    const { isLoading: creating, mutate: createDeposit } = useCreateDeposit();

    const schema = yup.object().shape({
        relatedAgreements: yup
            .array()
            .min(1, 'Please select at least one agreement.')
            .required('Please select at least one agreement.')
    });

    const methods = useForm<FormData>({
        defaultValues: { relatedAgreements: [] },
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(schema)
    });

    const {
        formState: { errors },
        setValue,
        watch
    } = methods;

    const agreementIdsOptions = useMemo(
        () =>
            data?.agreements?.map((item) => ({
                agreementStatus: item.agreementStatus as string,
                agreementType: item.agreementType as string,
                filterType: `${item.uploaderName} - ${
                    !item.hasBespokeTestingRequirements &&
                    !item.hasOptedOutOfVVStorage
                        ? 'Cloud'
                        : 'On-Prem'
                }`,
                hasBespokeTestingRequirements:
                    item.hasBespokeTestingRequirements as boolean,
                hasOptedOutOfVVStorage: item.hasOptedOutOfVVStorage as boolean,
                id: item.agreementId as unknown as string,
                isMultiAgreementType: item.isMultiAgreementType as boolean,
                label: `${item.agreementNumber} - ${item.softwarePackageName}`,
                uploadType:
                    !item.hasBespokeTestingRequirements &&
                    !item.hasOptedOutOfVVStorage
                        ? 'Cloud'
                        : 'On-Prem',
                uploaderName: item.uploaderName as string,
                value: item.agreementId as unknown as string,
                viewingPartyCanCreateDeposit:
                    item.viewingPartyCanCreateDeposit as boolean
            })),
        [data?.agreements]
    );

    const submit = useCallback(
        (formData: any) => {
            formData = formData.relatedAgreements.map((item: string) =>
                parseInt(item)
            );

            queryClient.invalidateQueries(['use-deposit-details-v2']);
            createDeposit(
                { relatedAgreements: { relatedAgreements: formData } },
                {
                    onError() {
                        toastFunctions.error(t('toast.error'));
                    },
                    onSuccess(res) {
                        toastFunctions.success(t('toast.success'));
                        localStorage.setItem('depositId', res.data.depositId);
                        goTo(
                            `/deposit-now/${res.data.depositId}/contact-details`
                        );
                    }
                }
            );
        },
        [createDeposit, goTo, queryClient, t]
    );

    const agreements = watch('relatedAgreements', []);

    const handleAgreementToggle = (agreementId: string, filterType: string) => {
        const currentSelections = [...agreements];
        const index = currentSelections.indexOf(agreementId);

        if (index > -1) {
            if (agreements.length === 1) {
                setFilter('none');
            }
            // Remove if already selected
            currentSelections.splice(index, 1);
        } else {
            // Add if not selected
            setFilter(filterType);
            currentSelections.push(agreementId);
        }

        setValue('relatedAgreements', currentSelections);
        setSelectedAll(false);
    };

    // const handleSelectAll = () => {
    //     if (selectedAll || agreements.length === filteredAgreements.length) {
    //         // Deselect all
    //         setValue('relatedAgreements', []);
    //         setSelectedAll(false);
    //     } else {
    //         // Select all visible
    //         const allIds = filteredAgreements.map(
    //             (agreement) => agreement.value
    //         );
    //         setValue('relatedAgreements', allIds);
    //         setSelectedAll(true);
    //     }
    // };

    // Filter agreements based on search and dropdown filters
    const filteredAgreements = useMemo(() => {
        if (!agreementIdsOptions) return [];

        return agreementIdsOptions.filter((agreement) => {
            const matchesSearch =
                searchTerm === '' ||
                agreement.label
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase());

            const matchesCountry =
                selectedCountry === 'All' ||
                agreement.uploaderName === selectedCountry;

            const matchesType =
                selectedType === 'All' || agreement.uploadType === selectedType;

            return matchesSearch && matchesCountry && matchesType;
        });
    }, [agreementIdsOptions, searchTerm, selectedCountry, selectedType]);

    // Get unique countries and types for dropdown filters
    // const countries = useMemo(() => {
    //     if (!agreementIdsOptions) return [];
    //     const uniqueCountries = new Set(
    //         agreementIdsOptions.map((a) => a.uploaderName)
    //     );
    //     return ['All', ...Array.from(uniqueCountries)];
    // }, [agreementIdsOptions]);

    // const types = useMemo(() => {
    //     if (!agreementIdsOptions) return [];
    //     const uniqueTypes = new Set(
    //         agreementIdsOptions.map((a) => a.uploadType)
    //     );
    //     return ['All', ...Array.from(uniqueTypes)];
    // }, [agreementIdsOptions]);

    return (
        <FormProvider {...methods}>
            <form className="mt-4" onSubmit={methods.handleSubmit(submit)}>
                <DNStep
                    containerClasses="flex flex-col gap-4 mb-2"
                    description={t('deposit-now-stage-one.description')}
                    displayAgreements={false}
                    disableBack
                    loading={isLoading}
                    submitting={creating}
                    title={t('deposit-now-stage-one.title')}
                >
                    <div className="flex items-center gap-4 mb-6">
                        <div className="relative flex-grow">
                            <input
                                type="text"
                                className="w-full h-10 py-2 pl-10 pr-4 text-sm text-black placeholder:text-general-grey-grey-70 border border-general-grey-grey-10 bg-general-grey-grey-10 rounded-lg focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-escode-neonblue-neonblue-100"
                                placeholder="Search Agreements..."
                                value={searchTerm}
                                tabIndex={0}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-5 h-5 text-general-grey-grey-70"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                    />
                                </svg>
                            </div>
                        </div>

                        {/* <div className="flex items-center gap-2">
                            <button
                                type="button"
                                className={cn(
                                    'flex items-center gap-2 py-2 px-4 border border-general-grey-grey-40 rounded-lg',
                                    selectedAll
                                        ? 'bg-brand-escode-neonblue-neonblue-100 text-white'
                                        : 'bg-white'
                                )}
                                onClick={handleSelectAll}
                            >
                                <input
                                    type="checkbox"
                                    className="accent-brand-escode-neonblue-neonblue-100 w-4 h-4"
                                    checked={
                                        selectedAll ||
                                        (agreements.length > 0 &&
                                            agreements.length ===
                                                filteredAgreements.length)
                                    }
                                    readOnly
                                />
                                <span>Select All</span>
                            </button>

                            <div className="relative">
                                <select
                                    className="appearance-none bg-white py-2 pl-4 pr-8 border border-general-grey-grey-40 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-escode-neonblue-neonblue-100"
                                    value={selectedCountry}
                                    onChange={(e) =>
                                        setSelectedCountry(e.target.value)
                                    }
                                >
                                    {countries.map((country) => (
                                        <option key={country} value={country}>
                                            Datacentre: {country}
                                        </option>
                                    ))}
                                </select>
                                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4 text-general-grey-grey-60"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 9l-7 7-7-7"
                                        />
                                    </svg>
                                </div>
                            </div>

                            <div className="relative">
                                <select
                                    className="appearance-none bg-white py-2 pl-4 pr-8 border border-general-grey-grey-40 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-escode-neonblue-neonblue-100"
                                    value={selectedType}
                                    onChange={(e) =>
                                        setSelectedType(e.target.value)
                                    }
                                >
                                    {types.map((type) => (
                                        <option key={type} value={type}>
                                            Type: {type}
                                        </option>
                                    ))}
                                </select>
                                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4 text-general-grey-grey-60"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 9l-7 7-7-7"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div> */}
                    </div>

                    <div className="flex flex-wrap gap-3 mb-4">
                        {filteredAgreements.map((agreement, i) => (
                            <div
                                key={agreement.id}
                                className={cn(
                                    'py-2 px-4 rounded-full border text-sm font-medium transition-colors duration-200 whitespace-nowrap overflow-hidden text-ellipsis max-w-xs cursor-pointer',
                                    agreements.includes(agreement.value)
                                        ? 'bg-brand-escode-neonblue-neonblue-10 text-brand-escode-neonblue-neonblue-100 border-brand-escode-neonblue-neonblue-100'
                                        : 'bg-white text-general-grey-grey-90 border-general-grey-grey-40 hover:bg-general-grey-grey-20',
                                    agreement.filterType !== filter && 'hidden',
                                    filter === 'none' && 'flex'
                                )}
                                onClick={() =>
                                    handleAgreementToggle(
                                        agreement.value,
                                        agreement.filterType
                                    )
                                }
                                tabIndex={i + 1}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        handleAgreementToggle(
                                            agreement.value,
                                            agreement.filterType
                                        );
                                    }
                                }}
                                title={`${agreement.label} (${agreement.uploadType}) (${agreement.uploaderName})`}
                            >
                                {agreements.includes(agreement.value) && (
                                    <>
                                        <FontAwesomeIcon
                                            icon={faCheck}
                                            className="text-brand-escode-neonblue-neonblue-100 mr-1"
                                        />
                                    </>
                                )}
                                <span className="truncate">
                                    {agreement.label}
                                </span>
                            </div>
                        ))}
                    </div>

                    {errors?.relatedAgreements && (
                        <div className="text-sm text-general-red-red-100 mb-4">
                            {errors.relatedAgreements.message}
                        </div>
                    )}
                </DNStep>
            </form>
        </FormProvider>
    );
};

export default AgreementStep;
